import { useState } from 'react'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Card, Col, Divider, Flex, Row, Tag, Tooltip, theme } from 'antd'

import { History } from '../../../atoms/history/types'
import { path } from '../../../../../libs'
import {
  SystemComment,
  verificationStatus,
  verificationType,
} from '../../../const'
import { ChangeStatusModal } from './ChangeStatusModal'
import { formatDate } from 'src/libs/formatDates'

const DOCUMENT_TYPE_COLORS: Record<string, string> = {
  [verificationType.verificationRequestTypeVerification()]: 'gray',
  [verificationType.verificationRequestTypeProfileChange()]: 'purple',
  [verificationType.verificationRequestTypeUpdatingDocument()]: 'gold',
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  [verificationType.verificationRequestTypeVerification()]: 'Verification',
  [verificationType.verificationRequestTypeProfileChange()]: 'Profile change',
  [verificationType.verificationRequestTypeUpdatingDocument()]: 'Update doc',
}

const getDocumentTypeTagColor = (documentType: string): string =>
  DOCUMENT_TYPE_COLORS[documentType] ?? 'default'

const getDocumentTypeLabel = (documentType: string): string =>
  DOCUMENT_TYPE_LABELS[documentType] ?? documentType

const getTagColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'processing'
    case 'accepted':
      return 'success'
    case 'false':
    case undefined:
    case 'rejected':
      return 'error'
    case 'expired':
      return 'warning'
    default:
      return 'processing'
  }
}

type HistoryCardProps = {
  item: History
  isSelected: boolean
  onClick: () => void
  marginBottom: number
  currentRequestId: string
}

export const HistoryCard = ({
  item,
  isSelected,
  onClick,
  marginBottom,
  currentRequestId,
}: HistoryCardProps) => {
  const { token } = theme.useToken()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isDubCheck =
    item.systemComment === SystemComment.AUTO_REJECTED_BY_DUPLICATE &&
    item.status === verificationStatus.verificationRequestStatusRejected()

  return (
    <>
      <ChangeStatusModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        requestId={currentRequestId}
      />
      <Card
        size="small"
        style={{
          marginBottom,
          background: isSelected ? token.colorFillAlter : undefined,
          cursor: 'pointer',
        }}
        title={
          <Flex justify="space-between" align="center">
            <span>{formatDate(item.createdAt)}</span>
            <Tooltip
              title={
                <>
                  <div>
                    {item.status} —{' '}
                    {formatDate(
                      item.manager ? item.manager.createdAt : item.createdAt,
                    )}
                    {isDubCheck
                      ? 'by Dub-check'
                      : item.manager &&
                        ` by manager ${item.manager.user.email}`}
                  </div>
                </>
              }
            >
              <InfoCircleOutlined />
            </Tooltip>
          </Flex>
        }
        onClick={onClick}
      >
        <Row gutter={[0, token.marginXS]} style={{ flexDirection: 'column' }}>
          <Col
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Tag color={getDocumentTypeTagColor(item.documentType)}>
              {getDocumentTypeLabel(item.documentType)}
            </Tag>
            <Tag color={getTagColor(item.status)}>{item.status}</Tag>
            {(item.requestId === currentRequestId ||
              item.status === 'accepted') && (
              <EditOutlined
                style={{ cursor: 'pointer', marginLeft: 0 }}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsModalOpen(true)
                }}
              />
            )}
          </Col>

          <Col>
            verify request:{' '}
            <a
              href={path.verificationsV2.details(item.requestId)}
              target="_blank"
            >
              {item.requestId}
            </a>
          </Col>
          <Divider style={{ margin: `${token.marginXS}px 0` }} />
          <Col>full name: {item.fullName}</Col>
          <Col>date of birth: {item.dateOfBirth}</Col>
          <Col>country: {item.country}</Col>
          <Col>doc number: {item.documentNumber}</Col>
          <Col>expiration date: {item.expirationDate}</Col>
          <Divider style={{ margin: `${token.marginXS}px 0` }} />
          <Col>
            {item.reason && <p>reason: {item.reason}</p>}
            {item.comment && <p>comment: {item.comment}</p>}
          </Col>
        </Row>
      </Card>
    </>
  )
}
